
// LiveStream.jsx (updated version)
import React from "react";
import {
  Play,
  Calendar,
  ChevronRight,
  ArrowLeft,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { useVideos } from "../../hooks/useVideos";
import UpcomingEvents from "./UpcomingEvents";

// Create a client
const queryClient = new QueryClient();

// Wrapper component with QueryClientProvider
const LiveStreamWithProvider = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <LiveStream />
    </QueryClientProvider>
  );
};

const LiveStream = () => {
  const navigate = useNavigate();
  const { videos, loading, page, totalPages, loadMore } = useVideos();

  // ── Helpers ──────────────────────────────────────────────────────────────
  const formatDateDMY = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-GB");
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  const getTimeAgo = (dateString) => {
    const now = new Date();
    const past = new Date(dateString);
    const diffInSeconds = Math.floor((now - past) / 1000);

    if (diffInSeconds < 60) return `${diffInSeconds} seconds ago`;
    const diffInMinutes = Math.floor(diffInSeconds / 60);
    if (diffInMinutes < 60)
      return `${diffInMinutes} minute${diffInMinutes > 1 ? "s" : ""} ago`;
    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24)
      return `${diffInHours} hour${diffInHours > 1 ? "s" : ""} ago`;
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7)
      return `${diffInDays} day${diffInDays > 1 ? "s" : ""} ago`;
    const diffInWeeks = Math.floor(diffInDays / 7);
    if (diffInWeeks < 4)
      return `${diffInWeeks} week${diffInWeeks > 1 ? "s" : ""} ago`;
    const diffInMonths = Math.floor(diffInDays / 30);
    if (diffInMonths < 12)
      return `${diffInMonths} month${diffInMonths > 1 ? "s" : ""} ago`;
    const diffInYears = Math.floor(diffInDays / 365);
    return `${diffInYears} year${diffInYears > 1 ? "s" : ""} ago`;
  };

  const getYouTubeThumbnail = (url) => {
    if (!url) return null;
    const regExp =
      /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    const videoId = match && match[2].length === 11 ? match[2] : null;
    return videoId
      ? `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`
      : null;
  };

  // ── Navigation ────────────────────────────────────────────────────────────
  // Navigate to detail page — passes video list via state for lookup
  const handleVideoClick = (item) => {
    navigate(`/live/${item._id}`, { state: { videos } });
  };

  const handleBack = () => navigate(-1);

  // ── VideoCard ─────────────────────────────────────────────────────────────
  const VideoCard = ({ item }) => {
    const thumbnailUrl = getYouTubeThumbnail(item.link);

    return (
      <div
        className="bg-white rounded-3xl overflow-hidden mb-6 border border-gray-200 shadow-lg hover:shadow-xl transition-shadow cursor-pointer"
        onClick={() => handleVideoClick(item)}
      >
        <div className="relative aspect-video group">
          <img
            src={
              thumbnailUrl ||
              "https://via.placeholder.com/600x340/E5E7EB/9CA3AF?text=Live+Session"
            }
            alt={item.title}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-14 h-14 bg-black/60 backdrop-blur-sm rounded-full flex items-center justify-center border border-white/30 group-hover:scale-110 transition-transform">
              <Play className="w-6 h-6 text-white ml-1" fill="white" />
            </div>
          </div>
          <div className="absolute bottom-3 right-3 bg-black/80 backdrop-blur-sm px-2 py-1 rounded-lg text-[10px] text-white font-medium">
            {formatTime(item.createdAt)}
          </div>
        </div>

        <div className="p-4">
          <h3 className="text-gray-900 text-xl font-bold mb-1 line-clamp-1">
            {item.title}
          </h3>
          <p className="text-gray-500 text-sm mb-4">
            LIVE Recording {formatDateDMY(item.createdAt)}
          </p>

          <div className="flex items-center justify-between">
            <div className="bg-green-50 text-green-700 px-3 py-1.5 rounded-xl flex items-center gap-2 text-xs font-medium">
              <Calendar className="w-4 h-4" />
              {getTimeAgo(item.createdAt)}
            </div>

            <button
              onClick={(e) => {
                e.stopPropagation();
                handleVideoClick(item);
              }}
              className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-xl flex items-center gap-2 text-sm font-bold transition-all hover:scale-105"
            >
              <div className="w-5 h-5 bg-white rounded-full flex items-center justify-center">
                <Play className="w-3 h-3 text-green-600 ml-0.5" fill="green" />
              </div>
              Watch Now
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="bg-gray-50 text-gray-900 pb-10">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 z-10">
        <div className="lg:hidden flex items-center p-4 relative">
          <button
            onClick={handleBack}
            className="hover:bg-gray-100 rounded-full"
          >
            <ArrowLeft className="w-6 h-6 text-gray-700" />
          </button>
          <h1 className="absolute left-1/2 -translate-x-1/2 text-xl font-bold text-gray-900">
            Live Streams
          </h1>
        </div>
      </div>

      {/* Upcoming Events Section */}
      <UpcomingEvents />

      {/* Recent Live Sessions Row */}
      <div className="flex items-center px-5 justify-between">
        <h2 className="text-xl font-semibold mt-4 text-gray-800">
          Recent Live Sessions
        </h2>
        {/* <button className="text-green-600 text-sm font-medium hover:text-green-700 transition-colors flex items-center gap-1">
          See All <ChevronRight className="w-4 h-4" />
        </button> */}
      </div>

      <div className="px-5 py-6">
        {/* Responsive Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {videos.map((video) => (
            <VideoCard key={video._id} item={video} />
          ))}
        </div>

        {/* Load More Button */}
        {page < totalPages && (
          <div className="text-center mt-8">
            <button
              onClick={loadMore}
              disabled={loading}
              className="bg-white border border-gray-300 mb-14 sm:mb-0 text-gray-700 px-10 py-3 rounded-2xl hover:bg-gray-50 hover:border-gray-400 transition-all font-semibold shadow-sm"
            >
              {loading ? "Loading..." : "Load More Videos"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default LiveStreamWithProvider;